import { AgentFaq, AgentKnowledgeBase, AgentOwnerInfo, AgentRelatedLink } from '../../../../domain/agents/models/Agent';
import { ConversationStarterLibrary, ConversationStarterItem } from '../../../../domain/chat/models/ConversationStarterLibrary';


type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return null;
    }
    return value as UnknownRecord;
};

const asStringArray = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const result = value.filter((item): item is string => typeof item === 'string');
    return result.length > 0 ? result : undefined;
};



const parseFaqs = (value: unknown): AgentFaq[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const result: AgentFaq[] = value.reduce<AgentFaq[]>((acc, item) => {
        const rec = asRecord(item);
        if (!rec || typeof rec.question !== 'string' || typeof rec.answer !== 'string') {
            return acc;
        }
        acc.push({ question: rec.question, answer: rec.answer });
        return acc;
    }, []);
    return result.length > 0 ? result : undefined;
};

const parseOwnerInfo = (value: unknown): AgentOwnerInfo[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const result: AgentOwnerInfo[] = value.reduce<AgentOwnerInfo[]>((acc, item) => {
        const rec = asRecord(item);
        if (!rec) {
            return acc;
        }

        const userPrincipalName =
            typeof rec.userPrincipalName === 'string'
                ? rec.userPrincipalName
                  : typeof rec.mail === 'string'
                    ? rec.mail
                    : '';

        acc.push({
            userId: typeof rec.userId === 'string' ? rec.userId : '',
            givenName: typeof rec.givenName === 'string' ? rec.givenName : '',
            surName: typeof rec.surName === 'string' ? rec.surName : '',
            userPrincipalName,
            mail: typeof rec.mail === 'string' ? rec.mail : '',
            photoUrl: typeof rec.photoUrl === 'string' ? rec.photoUrl : '',
        });
        return acc;
    }, []);
    return result.length > 0 ? result : undefined;
};

const parseRelatedLinks = (value: unknown): AgentRelatedLink[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const result: AgentRelatedLink[] = value.reduce<AgentRelatedLink[]>((acc, item) => {
        const rec = asRecord(item);
        if (!rec || typeof rec.title !== 'string' || typeof rec.link !== 'string') {
            return acc;
        }
        acc.push({ title: rec.title, link: rec.link });
        return acc;
    }, []);
    return result.length > 0 ? result : undefined;
};

const parseKnowledgeBase = (value: unknown): AgentKnowledgeBase | undefined => {
    const rec = asRecord(value);
    if (!rec) {
        return undefined;
    }
    return {
        content: typeof rec.content === 'string' ? rec.content : undefined,
    };
};

const parsePromptLibrary = (value: unknown): ConversationStarterLibrary | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const result = value.reduce<ConversationStarterLibrary>((groupsAcc, groupItem) => {
        const groupRecord = asRecord(groupItem);
        if (!groupRecord || typeof groupRecord.category !== 'string' || !Array.isArray(groupRecord.items)) {
            return groupsAcc;
        }

        const items = groupRecord.items.reduce<ConversationStarterItem[]>((itemsAcc, item) => {
            const itemRecord = asRecord(item);
            if (!itemRecord || typeof itemRecord.prompt !== 'string') {
                return itemsAcc;
            }

            itemsAcc.push({
                title: typeof itemRecord.title === 'string' ? itemRecord.title : undefined,
                prompt: itemRecord.prompt,
                requiresDocument: itemRecord.requiresDocument === true,
                followUps: asStringArray(itemRecord.followUps),
                isLargeBlock: itemRecord.isLargeBlock === true,
            });
            return itemsAcc;
        }, []);

        groupsAcc.push({
            category: groupRecord.category,
            items,
        });
        return groupsAcc;
    }, []);

    return result.length > 0 ? result : undefined;
};


export interface ParsedNexusAgentMetadata {
    agentIconUrl?: string;
    cardShortDescription?: string;
    disclaimer?: string;
    promptLibrary?: ConversationStarterLibrary;
    faqs?: AgentFaq[];
    ownerBlurb?: string;
    ownerInfo?: AgentOwnerInfo[];
    supportAddress?: string;
    capabilities?: string[];
    relatedLinks?: AgentRelatedLink[];
    knowledgeBase?: AgentKnowledgeBase;
    ownershipMode?: 'owners' | 'support';
    allowFileSearchAtConversationLevel?: boolean;
    allowCodeInterpreterAtConversationLevel?: boolean;
    oldAssistantId?: string;
}

export const parseNexusAgentMetadata = (raw: Record<string, unknown>): ParsedNexusAgentMetadata => {
    const ownershipModeRaw = raw.ownershipMode;
    const ownershipMode: 'owners' | 'support' | undefined =
        ownershipModeRaw === 'owners' || ownershipModeRaw === 'support' ? ownershipModeRaw : undefined;

    return {
        agentIconUrl: typeof raw.agentIconUrl === 'string' ? raw.agentIconUrl : undefined,
        cardShortDescription: typeof raw.cardShortDescription === 'string' ? raw.cardShortDescription : undefined,
        disclaimer: typeof raw.disclaimer === 'string' ? raw.disclaimer : undefined,
        promptLibrary: parsePromptLibrary(raw.promptLibrary),
        faqs: parseFaqs(raw.faqs),
        ownerBlurb: typeof raw.ownerBlurb === 'string' ? raw.ownerBlurb : undefined,
        ownerInfo: parseOwnerInfo(raw.agentOwnerInfo),
        supportAddress: typeof raw.supportAddress === 'string' ? raw.supportAddress : undefined,
        capabilities: asStringArray(raw.capabilities),
        relatedLinks: parseRelatedLinks(raw.relatedLinks),
        knowledgeBase: parseKnowledgeBase(raw.agentKnowledgeBase),
        ownershipMode,
        allowFileSearchAtConversationLevel: raw.allowFileSearchAtConversationLevel === true,
        allowCodeInterpreterAtConversationLevel: raw.allowCodeInterpreterAtConversationLevel === true,
        oldAssistantId: typeof raw.oldAssistantId === 'string' ? raw.oldAssistantId : undefined,
    };
};
